import {
  theDomWatcher,
  handleNewsletterSubscribed,
  trackNewsletterSignup,
  initProductDescriptions,
} from "@theme/helpers";

import { ThemeMotion } from "@theme/theme-motion";

new ThemeMotion().init();

const featureLoaders = [
  {
    label: "cart",
    selectors: ["[data-cart-drawer]", "[data-cart-container]", "[data-open-cart]"],
    observe: true,
    loader: () => import("@theme/cart"),
  },
  {
    label: "wishlist",
    selectors: ["[wishlist-button]", "[wishlist-grid]", "[side-cart-wishlist-grid]"],
    observe: true,
    loader: () => import("@theme/wishlist"),
  },
  {
    label: "side-cart-tabs",
    selectors: ['.side-cart-tabs[role="tablist"]', "[data-cart-tab]"],
    observe: true,
    loader: () => import("@theme/tabs"),
  },
  {
    label: "toasty",
    selectors: ["body"],
    loader: () => import("@theme/toasty"),
  },
  {
    label: "page-transitions",
    selectors: ["body"],
    loader: () =>
      import("@theme/page-transitions").then(
        ({ PageTransitionController }) => new PageTransitionController(),
      ),
  },
  {
    label: "product-quick-add",
    selectors: ["[quick-add-qf]", "product-form"],
    observe: true,
    loader: () =>
      import("@theme/product-quick-add").then(({ ProductQuickAdd }) => new ProductQuickAdd()),
  },
  {
    label: "quickview",
    selectors: ["product-quick-view", "quickview-modal"],
    loader: () => import("@theme/quickview").then(({ ProductQuickView }) => new ProductQuickView()),
  },
  {
    label: "product-form",
    selectors: ["product-form"],
    observe: true,
    loader: () => import("@theme/option-selectors").then(({ ProductForm }) => new ProductForm()),
  },
  {
    label: "product-sticky-atc",
    selectors: ["[data-sticky-atc]", "[data-sticky-atc-observer-target]"],
    observe: true,
    loader: () =>
      import("@theme/product-sticky-atc").then(({ ProductStickyAtc }) => new ProductStickyAtc()),
  },
  {
    label: "product-card",
    selectors: ["product-card"],
    observe: true,
    loader: () => import("@theme/product-card").then(({ ProductCard }) => new ProductCard()),
  },
  {
    label: "predictive-search",
    selectors: ["predictive-search"],
    loader: () => import("@theme/search").then(({ SearchController }) => new SearchController()),
  },
  {
    label: "menu",
    selectors: ["#open-mobile-menu", ".mobile-menu-outer-wrapper"],
    loader: () => import("@theme/menu").then(({ MenuController }) => new MenuController()),
  },
  {
    label: "accordion",
    selectors: [".accordion"],
    loader: () =>
      import("@theme/accordion").then(({ AccordionController }) => new AccordionController()),
  },
  {
    label: "hash-accordion",
    selectors: ["body"],
    loader: () =>
      import("@theme/hash-accordion").then(
        ({ HashAccordionController }) => new HashAccordionController(),
      ),
  },
  {
    label: "product-quantity",
    selectors: ["[quantity-select]"],
    observe: true,
    loader: () =>
      import("@theme/product-quantity").then(({ ProductQuantity }) => new ProductQuantity()),
  },
  {
    label: "header-layout",
    selectors: ["[data-header-layout]"],
    loader: () =>
      import("@theme/header-layout").then(
        ({ HeaderLayoutController }) => new HeaderLayoutController(),
      ),
  },
  {
    label: "header-scroll",
    selectors: [".header-outer-shopify-wrapper"],
    loader: () =>
      import("@theme/header-scroll").then(
        ({ HeaderScrollController }) => new HeaderScrollController(),
      ),
  },
  {
    label: "product-media-kit",
    selectors: ["[data-media-kit-download]"],
    observe: true,
    loader: () =>
      import("@theme/product-media-kit").then(
        ({ ProductMediaKitDownload }) => new ProductMediaKitDownload(),
      ),
  },
  {
    label: "product-delivery-countdown",
    selectors: ["[data-delivery-countdown]"],
    observe: true,
    loader: () =>
      import("@theme/product-delivery-countdown").then(
        ({ ProductDeliveryCountdown }) => new ProductDeliveryCountdown(),
      ),
  },
  {
    label: "size-guide",
    selectors: ["[data-size-guide-drawer]", "[data-size-guide-trigger]"],
    loader: () => import("@theme/size-guide").then(({ SizeGuideDrawer }) => new SizeGuideDrawer()),
  },
  {
    label: "insert-covers-learn-more",
    selectors: ["[data-insert-learn-more-drawer]", "[data-insert-learn-more-trigger]"],
    loader: () =>
      import("@theme/insert-covers-learn-more").then(
        ({ InsertCoversLearnMoreDrawer }) => new InsertCoversLearnMoreDrawer(),
      ),
  },
  {
    label: "modal-controller",
    selectors: ["[data-modal-trigger]", "[data-modal]"],
    loader: () =>
      import("@theme/modal-controller").then(({ ModalController }) => new ModalController()),
  },
  {
    label: "blog-jump-buttons",
    selectors: ["[data-blog-jump-buttons]"],
    observe: true,
    loader: () =>
      import("@theme/blog-jump-buttons").then(
        ({ BlogJumpButtonsController }) => new BlogJumpButtonsController(),
      ),
  },
  {
    label: "shopify-forms-overrides",
    selectors: ["[data-form-root='true']", "shopify-forms-embed"],
    observe: true,
    loader: () => import("@theme/shopify-forms-overrides"),
  },
  {
    label: "faq-search",
    selectors: [".fuse-faqs-search"],
    observe: true,
    loader: () =>
      import("@theme/faq-search").then(({ FAQSearchController }) => {
        document
          .querySelectorAll(".fuse-faqs-search")
          .forEach((root) => FAQSearchController.init(root));
      }),
  },
  {
    // Re-open the Klaviyo signup popup after the Cookiebot banner is dismissed
    // (the banner sits on top of the popup and closing it also closes the popup).
    label: "klaviyo-cookiebot-fix",
    selectors: ["body"],
    loader: () => import("@theme/klaviyo-cookiebot-fix"),
  },
];

featureLoaders.forEach(({ selectors, loader, label, observe }) =>
  theDomWatcher(selectors, loader, { label, observe }),
);

theDomWatcher(
  "[data-blaze-slider]",
  () =>
    import("@theme/blaze-slider-controller").then(({ blazeSliderController }) => {
      blazeSliderController.init(document);
      return blazeSliderController;
    }),
  { label: "blaze-slider", observe: true },
);

theDomWatcher("form.newsletter-form", handleNewsletterSubscribed, { label: "newsletter-form" });
theDomWatcher('input[name="contact[email]"]', trackNewsletterSignup, {
  label: "newsletter-signup",
});
theDomWatcher(
  ["[read-more-container]", "[data-product-description-preview]"],
  initProductDescriptions,
  {
    label: "read-more",
  },
);
